import { UzButton, useBranding } from '@utazzo/ui';
import { ArrowLeft, Printer } from 'lucide-react';
import React from 'react';
import { Link, useParams } from 'react-router-dom';

import { amountToWords, formatDateDMY, formatInrPaise } from '../invoices.helpers';
import { useCreditNote } from '../use-invoices';

import type { CreditNoteDetail } from '@utazzo/shared';

/**
 * Full-page GST credit-note sheet (route /invoices/credit-notes/:id/print).
 * Mirrors the tax-invoice print sheet in InvoiceDetailPage: a single bordered
 * outer table with thin black borders, compact cells and print-oriented
 * styling. The `@media print` block scopes the printed output to
 * `#invoice-sheet`, hiding the app chrome so the note prints on its own.
 */

const SHEET_CSS = `
#invoice-sheet {
  color: #000;
  background: #fff;
  font-family: Calibri, Candara, Segoe, "Segoe UI", Arial, sans-serif;
  font-size: 13px;
  line-height: 1.35;
}
#invoice-sheet .inv-title {
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
}
#invoice-sheet .inv-logo {
  width: 240px;
  max-width: 60%;
  height: auto;
  margin: 4px auto 6px;
  display: block;
}
#invoice-sheet table.inv {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
}
#invoice-sheet table.inv > tbody > tr > td {
  border: 1px solid #000;
  padding: 3px 6px;
  vertical-align: top;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}
#invoice-sheet .inv-c { text-align: center; }
#invoice-sheet .inv-r { text-align: right; }
#invoice-sheet .inv-b { font-weight: 700; }
#invoice-sheet .inv-note { font-size: 10px; }

@media print {
  body * { visibility: hidden; }
  #invoice-sheet, #invoice-sheet * { visibility: visible; }
  #invoice-sheet {
    position: absolute;
    inset: 0;
    margin: 0;
    padding: 24px;
    box-shadow: none !important;
    border: none !important;
    max-width: none !important;
    width: 100%;
  }
  .invoice-no-print { display: none !important; }
  @page { margin: 12mm; }
}
`;

/** Human-readable place of supply from the enum + a state name when we have one. */
const placeOfSupplyLabel = (cn: CreditNoteDetail): string => {
  const stateName = cn.buyer?.stateName ?? cn.billingEntity.stateName ?? null;
  switch (cn.placeOfSupply) {
    case 'INTRA':
      return `Intra-state${stateName ? ` (${stateName})` : ''}`;
    case 'INTER':
      return `Inter-state${stateName ? ` (${stateName})` : ''}`;
    case 'EXPORT':
      return 'Export / Out of India';
    default:
      return cn.placeOfSupply;
  }
};

export const CreditNotePrintPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const creditNoteQuery = useCreditNote(id);
  const branding = useBranding();

  if (creditNoteQuery.isLoading) {
    return <div className="p-8 text-sm text-zinc-500">Loading credit note...</div>;
  }
  if (creditNoteQuery.isError || !creditNoteQuery.data) {
    return (
      <div className="space-y-4 p-8">
        <p className="text-sm text-red-600">Could not load this credit note.</p>
        <Link to="/invoices" className="text-primary text-sm underline">
          Back to invoices
        </Link>
      </div>
    );
  }

  const cn = creditNoteQuery.data;
  const entity = cn.billingEntity;
  const buyer = cn.buyer;
  const isCancelled = cn.status === 'CANCELLED';
  const docTitle = isCancelled ? 'Credit Note (Cancelled)' : 'Credit Note';

  const customerName = buyer?.name ?? cn.customerName ?? '-';
  const customerCityState = buyer ? [buyer.city, buyer.stateName].filter(Boolean).join(', ') : '';
  const customerGst = buyer?.gstNumber ?? cn.customerGstin ?? null;
  const customerStateCode = buyer?.stateCode ?? null;

  const entityCityState = [entity.city, entity.stateName].filter(Boolean).join(', ');
  const logoUrl = entity.logoUrl ?? branding.logoUrl ?? null;

  return (
    <div className="space-y-4">
      <style>{SHEET_CSS}</style>

      {/* Toolbar (never printed). */}
      <div className="invoice-no-print flex items-center justify-between">
        <Link
          to={`/invoices/${cn.invoiceId}`}
          className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to invoice
        </Link>
        <UzButton leftIcon={<Printer className="h-4 w-4" />} onClick={() => window.print()}>
          Print / Save as PDF
        </UzButton>
      </div>

      {/* The printable sheet - legacy bordered layout, matching the tax invoice. */}
      <div
        id="invoice-sheet"
        className="mx-auto max-w-3xl border border-zinc-200 bg-white p-8 shadow-sm"
      >
        <div className="inv-title">{docTitle}</div>

        <table className="inv">
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
          </colgroup>
          <tbody>
            {/* Title tag row. */}
            <tr>
              <td colSpan={3} className="inv-b inv-c">
                {docTitle}
              </td>
              <td className="inv-b inv-r">Original For Receiver</td>
            </tr>

            {/* Masthead: brand logo + legal name (from settings). */}
            <tr>
              <td colSpan={4} className="inv-c">
                {logoUrl && <img className="inv-logo" src={logoUrl} alt={entity.legalName} />}
                <div className="inv-b">{entity.legalName}</div>
              </td>
            </tr>

            {/* Header: customer (left) + billing entity (right). */}
            <tr>
              <td colSpan={2}>
                <div className="inv-b">Customer Name:</div>
                <div>{customerName}</div>
                {buyer?.addressLine1 && <div>{buyer.addressLine1}</div>}
                {customerCityState && <div>{customerCityState}</div>}
                {customerGst && (
                  <div>
                    <span className="inv-b">GST No.:</span> {customerGst}
                  </div>
                )}
                {customerStateCode && (
                  <div>
                    <span className="inv-b">State Code:</span> {customerStateCode}
                  </div>
                )}
              </td>
              <td colSpan={2} className="inv-r">
                {entity.addressLine1 && <div>{entity.addressLine1}</div>}
                {entity.addressLine2 && <div>{entity.addressLine2}</div>}
                {entityCityState && <div>{entityCityState}</div>}
                {entity.phone && (
                  <div>
                    <span className="inv-b">Contact No.:</span> {entity.phone}
                  </div>
                )}
                {entity.email && (
                  <div>
                    <span className="inv-b">Email:</span> {entity.email}
                  </div>
                )}
                {entity.cin && (
                  <div>
                    <span className="inv-b">CIN No.</span> {entity.cin}
                  </div>
                )}
                <div>
                  <span className="inv-b">GST No.:</span> {entity.gstin}
                </div>
                {entity.stateCode && (
                  <div>
                    <span className="inv-b">State Code:</span> {entity.stateCode}
                  </div>
                )}
              </td>
            </tr>

            {/* Date / Credit Note No. */}
            <tr>
              <td className="inv-b">Date:</td>
              <td>{formatDateDMY(cn.issuedAt)}</td>
              <td className="inv-b">Credit Note No.:</td>
              <td>{cn.creditNoteNumber}</td>
            </tr>

            {/* Against invoice / HSN. */}
            <tr>
              <td className="inv-b">Against Invoice:</td>
              <td>{cn.invoiceNumber}</td>
              <td className="inv-b">HSN Code:</td>
              <td>{cn.hsn}</td>
            </tr>

            {/* Place of Supply / Pan No. */}
            <tr>
              <td className="inv-b">Place of Supply:</td>
              <td>{placeOfSupplyLabel(cn)}</td>
              <td className="inv-b">Pan No.:</td>
              <td>{entity.pan ?? '-'}</td>
            </tr>

            {/* Reason. */}
            <tr>
              <td className="inv-b">Reason:</td>
              <td colSpan={3}>{cn.reason}</td>
            </tr>

            {/* Particulars header. */}
            <tr>
              <td className="inv-b inv-c">S.No.</td>
              <td className="inv-b inv-c">Particulars</td>
              <td className="inv-b inv-c">Units</td>
              <td className="inv-b inv-c">Amount</td>
            </tr>

            {/* Single credited line. */}
            <tr>
              <td className="inv-b inv-c">1</td>
              <td className="inv-b">Credit against invoice {cn.invoiceNumber}</td>
              <td className="inv-c">1</td>
              <td className="inv-r">{formatInrPaise(cn.creditedTaxablePaise)}</td>
            </tr>

            {/* Amount breakdown. */}
            <tr>
              <td colSpan={3} className="inv-b inv-c">
                Taxable Value
              </td>
              <td className="inv-r">{formatInrPaise(cn.creditedTaxablePaise)}</td>
            </tr>
            <tr>
              <td colSpan={3} className="inv-b inv-c">
                CGST Reversed
              </td>
              <td className="inv-r">{formatInrPaise(cn.reversedCgstPaise)}</td>
            </tr>
            <tr>
              <td colSpan={3} className="inv-b inv-c">
                SGST Reversed
              </td>
              <td className="inv-r">{formatInrPaise(cn.reversedSgstPaise)}</td>
            </tr>
            <tr>
              <td colSpan={3} className="inv-b inv-c">
                IGST Reversed
              </td>
              <td className="inv-r">{formatInrPaise(cn.reversedIgstPaise)}</td>
            </tr>
            <tr>
              <td colSpan={3} className="inv-b inv-c">
                Total Credit
              </td>
              <td className="inv-b inv-r">{formatInrPaise(cn.totalPaise)}</td>
            </tr>

            {/* Amount in words. */}
            <tr>
              <td colSpan={4}>
                <span className="inv-b">Total Amount in Word:</span> {amountToWords(cn.totalPaise)}
              </td>
            </tr>

            {/* Footer note + Company Seal box. */}
            <tr>
              <td colSpan={3} className="inv-note">
                This is a system-generated credit note and does not require a signature.
              </td>
              <td className="inv-c">
                <div className="inv-b">Company Seal</div>
                <div style={{ height: 78 }} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
